import { html, TemplateResult } from "lit-html";
import { repeat } from "lit-html/directives/repeat";
import { I18nService } from "Services/I18nService";
import { getDocLocale } from "Helper/metaHelper";

import type { Booking, DetailedBooking } from "./fra-m-park-bookings-interfaces";
import { createHashFromBookingCode } from "./fra-m-park-bookings";

/** ####### HELPERS ####### */

/**
 * Renders park-booking-list
 *
 * @param {Booking} booking
 * @param {Function} bookingSelectionHandler - callback for when user selects a booking
 * @returns {TemplateResult}
 */
function renderBookingRow(booking: Booking, bookingSelectionHandler: Function): TemplateResult {
  return html`
    <dl class="fra-m-bookings-list">
      <div class="fra-e-list__entry">
        <dt class="fra-e-list__label fra-e-list__label___booking-code">${booking.bookingCode}</dt>
        <dt class="fra-e-list__label fra-e-aggregate fra-h-hide-mq2">
          <span>${booking.product}</span>
          <span>${formatDate(booking.arrival)} - ${formatDate(booking.departure)}</span>
          ${renderState(booking.canceled)}
        </dt>
        <dt class="fra-e-list__label fra-h-show-mq2">${booking.product}</dt>
        <dt class="fra-e-list__label fra-h-show-mq2 fra-e-list__label___period">
          ${formatDate(booking.arrival)} - ${formatDate(booking.departure)}
        </dt>
        <dt class="fra-e-list__label fra-h-show-mq2 fra-e-list__label___state">${renderState(booking.canceled)}</dt>
        <dd class="fra-e-list__desc fra-e-list__desc-link ">
          <a
            class="fra-e-link fra-e-link--with-icon fra-e-link--animated-right fra-bookings-js-list-item"
            href=${createHashFromBookingCode(booking.bookingCode)}
            @click=${(e: UIEvent) => {
              e.preventDefault();
              bookingSelectionHandler(booking.bookingCode, true);
            }}
          >
            <fra-e-icon class="fra-e-icon " icon-id="fra-icon-arrow-right"></fra-e-icon>
          </a>
        </dd>
      </div>
    </dl>
  `;
}

function renderState(canceled?: boolean): TemplateResult {
  if (canceled === undefined) return html``;

  return canceled
    ? html`
        <span class="fra-m-parking__state-canceled">
          ${I18nService.getInstance().getKey("fraport.travel.components.parkBooking.state.cancelled.label")}
        </span>
      `
    : html`
        <span class="fra-m-parking__state-active">
          ${I18nService.getInstance().getKey("fraport.travel.components.parkBooking.state.active.label")}
        </span>
      `;
}

function formatCurrency(price?: number): string {
  if (price === undefined) return "";

  const locale = getDocLocale();
  return new Intl.NumberFormat(locale, { style: "currency", currency: "EUR" }).format(price);
}

function getCountry(countryCode?: string): string | undefined {
  if (!countryCode) return "";

  const locale = getDocLocale();
  const regionNames = new Intl.DisplayNames(locale, { type: "region" });
  return regionNames.of(countryCode);
}

function formatDateTime(dateStr?: string): string {
  if (!dateStr) return "";

  const locale = getDocLocale();
  const options = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: !locale.startsWith("de"),
  } as const;
  const dateIn = new Date(dateStr);
  return dateIn.toLocaleDateString(locale, options).concat(locale.startsWith("de") ? " Uhr" : "");
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return "";

  const locale = getDocLocale();
  const options = { year: "numeric", month: "2-digit", day: "2-digit" } as const;
  const dateIn = new Date(dateStr);
  return dateIn.toLocaleDateString(locale, options);
}

/** ####### EXPORTS ####### */

export function parkBookingDataTemplate(booking: DetailedBooking | undefined): TemplateResult {
  if (booking !== undefined) {
    return html`
      <h2 class="fra-m-booking-section--headline fra-h1 fra-h1">${booking.product} ${booking.bookingCode}</h2>
      <fra-e-list class="fra-e-list">
        <dl class="fra-m-parking-data--list">
          <div class="fra-e-list__entry ">
            <dt class="fra-e-list__label">
              ${I18nService.getInstance().getKey("fraport.travel.components.parkBooking.bookingDate.label")}
            </dt>
            <dd class="fra-e-list__desc fra-e-list__desc-link ">${formatDate(booking.bookingTs)}</dd>
          </div>
          <div class="fra-e-list__entry ">
            <dt class="fra-e-list__label">
              ${I18nService.getInstance().getKey("fraport.travel.components.parkBooking.state.label")}
            </dt>
            <dd class="fra-e-list__desc fra-e-list__desc-link ">${renderState(booking.canceled)}</dd>
          </div>
          <div class="fra-e-list__entry ">
            <dt class="fra-e-list__label">
              ${I18nService.getInstance().getKey("fraport.travel.components.parkBooking.arrival.label")}
            </dt>
            <dd class="fra-e-list__desc fra-e-list__desc-link ">${formatDateTime(booking.arrival)}</dd>
          </div>
          <div class="fra-e-list__entry ">
            <dt class="fra-e-list__label">
              ${I18nService.getInstance().getKey("fraport.travel.components.parkBooking.departure.label")}
            </dt>
            <dd class="fra-e-list__desc fra-e-list__desc-link ">${formatDateTime(booking.departure)}</dd>
          </div>
          <div class="fra-e-list__entry ">
            <dt class="fra-e-list__label">
              ${I18nService.getInstance().getKey("fraport.travel.components.parkBooking.area.label")}
            </dt>
            <dd class="fra-e-list__desc fra-e-list__desc-link ">${booking.area}</dd>
          </div>
          <div class="fra-e-list__entry ">
            <dt class="fra-e-list__label">
              ${I18nService.getInstance().getKey("fraport.travel.components.parkBooking.price.label")}
            </dt>
            <dd class="fra-e-list__desc fra-e-list__desc-link ">${formatCurrency(booking.price)}</dd>
          </div>
        </dl>
      </fra-e-list>
    `;
  }

  return html``;
}

/**
 * Renders park-booking-customer-address
 *
 * @param {DetailedBooking.customerAddress} customerAddress
 * @returns {TemplateResult}
 */
export function parkBookingAddressTemplate(customerAddress: DetailedBooking["customerAddress"]): TemplateResult {
  if (customerAddress !== undefined) {
    return html`
      <span>${customerAddress.firstname} ${customerAddress.surname}</span>
      <br />
      <span>${customerAddress.street}</span>
      <br />
      <span>${customerAddress.zipcode} ${customerAddress.city}</span>
      <br />
      <span>${getCountry(customerAddress.country)}</span>
      <br />
    `;
  }

  return html``;
}

/**
 * Renders park-booking-list
 *
 * @param {Booking[]} data
 * @param {Function} showBookingData
 * @returns {TemplateResult}
 */
export const parkBookingTemplate = (data: Booking[], showBookingData: Function): TemplateResult => {
  return html`
    ${repeat(
      data,
      (booking: Booking) => booking.bookingCode,
      (booking: Booking) => renderBookingRow(booking, showBookingData),
    )}
  `;
};
